import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react"
import useApi from "modules/account"
import useGroupApi from "modules/groups"
import secureLocalStorage from "react-secure-storage"
import {
  ELEMENTARY_TO_REGULAR,
  GROUP_TYPES,
  USER_TYPES,
  SMASH_SESSION,
  STAFF_TYPES,
} from "common/constants"
import moment from "moment"
import { LOCAL_STORAGE_PREFIX } from "../common/constants"
import {
  bgOptions,
  fontFamilyOptions,
  fontOptions,
} from "common/textEditorConstants"
import { useThemeContext } from "./ThemeContext"
import storage from "utils/persistentStorage"
import { useAuthContext } from "./AuthContext"
import { isRunningFromIframe } from "common/functions"
import ContainerWrapper from "components/UI/ContainerWrapper"

export const StudentAuthContext = createContext()

export const useStudentAuthContext = () => {
  const context = useContext(StudentAuthContext)
  if (context === undefined) {
    throw new Error(
      "useStudentAuthContext must be used within a StudentAuthProvider"
    )
  }
  return context
}

export const StudentAuthProvider = ({ children }) => {
  const {
    userData,
    updateUserData: baseUpdateUserData,
    logout: baseLogout,
    setCacheRewardsNotifications,
  } = useAuthContext()

  const {
    getUserAllInformation,
    updateUserAudioSettings,
    getSessionState,
    verifyStatus,
  } = useApi()
  const { getSelectedGroup, getStudentGroups } = useGroupApi()
  const [answerTourOpened, setAnswerTourOpened] = useState(false)
  const [tourSkiped, setTourSkiped] = useState(false)
  const [selectedGroupData, setSelectedGroupData] = useState({
    groupId: -1,
    teachers: [],
    isSequentialContent: false,
  })
  const [audioSettings, setAudioSettings] = useState({
    enabledAudioEffects: true,
    volume: 50,
    playBackRate: 1,
  })
  const [textEditorSettings, setTextEditorSettings] = useState({
    fontSize: 1,
    fontFamily: 0,
    backgroundColor: 0,
    fillColor: 0,
    ruleEnabled: true,
  })
  const [isConfigurationCompleted, setIsConfigurationCompleted] =
    useState(false)

  const { setFontFamily, setFontSize, setBgColor } = useThemeContext()
  const [isRestoreCompleted, setIsRestoreCompleted] = useState(false)
  const isRestoring = useRef(false)
  const lastSessionState = useRef(null)

  const getPath = useCallback((userTypeId) => {
    const runningFromIframe = isRunningFromIframe()
    if (runningFromIframe) return true
    const isInvoicePage = window.location.pathname
      .toLowerCase()
      .includes("/payments/invoice")
    if (isInvoicePage) return true
    if (userTypeId === 6) {
      const currentPage = window.location.pathname
        .split("/")
        .pop()
        .toLowerCase()
      if (currentPage.includes("announcements")) {
        window.location.href =
          "/professor/announcements" + window.location.search
        return false
      } else {
        window.location.href = "/professor/groups"
        return false
      }
    } else if (userTypeId === 10 || userTypeId === 1) {
      window.location.href = "/professor/admin/index"
      return false
    } else if (userTypeId === 11) {
      window.location.href = "/institute/Usage.aspx"
      return false
    }
    return true
  }, [])

  const saveMainTeacherTimeZone = useCallback((groupData) => {
    if (!groupData || !groupData.teachers) {
      secureLocalStorage.setItem(`mainTeacherTimeZone`, "")
      return
    }
    const mainTeacher = groupData.teachers.find((t) => t.isMainTeacher)
    const timeZone = mainTeacher?.timeZone || ""
    secureLocalStorage.setItem(`mainTeacherTimeZone`, timeZone)
  }, [])

  const logout = useCallback(() => {
    setSelectedGroupData({
      groupId: -1,
      teachers: [],
      isSequentialContent: false,
    })
    secureLocalStorage.removeItem(`mainTeacherTimeZone`)
    baseLogout()
  }, [baseLogout])

  // Sync state with userData changes
  useEffect(() => {
    if (!userData || userData.userId <= 0) {
      // Reset student states
      setSelectedGroupData({
        groupId: -1,
        teachers: [],
        isSequentialContent: false,
      })
      setAudioSettings({
        enabledAudioEffects: true,
        volume: 50,
        playBackRate: 1,
      })
      setTextEditorSettings({
        fontSize: 1,
        fontFamily: 0,
        backgroundColor: 0,
        fillColor: 0,
        ruleEnabled: true,
      })
      setIsConfigurationCompleted(false)
      setIsRestoreCompleted(false)
      isRestoring.current = false
      lastSessionState.current = null
      return
    }

    if (userData.userTypeId === USER_TYPES.STUDENT) {
      setIsConfigurationCompleted(
        userData?.isConfigurationCompleted && !userData?.passwordUpdateRequired
      )
      if (isRestoreCompleted || isRestoring.current) {
        return
      }
      isRestoring.current = true
      const userId = userData.userId

      // 1. Rollback validation
      const rollbackUser = getPath(userData.userTypeId || 7)
      if (!rollbackUser) {
        console.log("Invalid user type, redirect to login")
        secureLocalStorage.removeItem("smashAuthToken")
        secureLocalStorage.removeItem(SMASH_SESSION)
        window.location.href = "/login?session=invalid-token"
        return
      }

      // Restore audio settings
      if (userData.audioSettings) {
        setAudioSettings(userData.audioSettings)
      }

      // Restore text editor settings
      if (userData.textEditorSettings) {
        setTextEditorSettings(userData.textEditorSettings)
        if (userData.textEditorSettings.fontFamily >= 0) {
          const selectedFontStyle =
            fontFamilyOptions[userData.textEditorSettings.fontFamily]
          const selectedFontSize =
            fontOptions[userData.textEditorSettings.fontSize]
          const selectedBgColor =
            bgOptions[userData.textEditorSettings.backgroundColor]
          setBgColor(selectedBgColor?.bg || "white")
          setFontFamily(selectedFontStyle?.name || "Poppins Regular")
          setFontSize(selectedFontSize?.fontSize || "18px")
        }
      }

      // 3. Staleness, verify status, and Restore selected group
      const checkSessionStalenessAndRestoreGroup = async () => {
        const currentPage = window.location.pathname
          .split("/")
          .pop()
          .toLowerCase()
        const isActivityPage = currentPage.includes("activity")
        const iframeFlag = isRunningFromIframe()
        let sessionState = null

        setCacheRewardsNotifications(true)
        if (isActivityPage) {
          const data = await storage.getItem(
            `${LOCAL_STORAGE_PREFIX}sessionState_${userId}`
          )
          sessionState = data || null
        } else {
          if (lastSessionState.current) {
            sessionState = lastSessionState.current
          } else if (!iframeFlag) {
            sessionState = await getSessionState()
            lastSessionState.current = sessionState
          } else {
            sessionState = window?.parent?.app?.sessionState
          }
        }

        if (sessionState) {
          await storage.setItem(
            `${LOCAL_STORAGE_PREFIX}sessionState_${userId}`,
            sessionState
          )
        }

        // Check if last update is more than 1 day
        const lastUpdate = userData.lastUpdate
        let isStale = false
        if (lastUpdate) {
          if (sessionState) {
            const parentLastUpdate = sessionState?.lastUpdate
            if (
              (parentLastUpdate && parentLastUpdate !== lastUpdate) ||
              sessionState?.updateRequired
            ) {
              isStale = true
            }
          } else {
            console.log("No parent session state found")
            const lastUpdateDate = moment(lastUpdate)
            const now = moment()
            const diff = now.diff(lastUpdateDate, "days")
            if (diff > 1) {
              isStale = true
            }
          }
        } else {
          isStale = true
        }

        let activeUser = userData
        if (isStale) {
          setCacheRewardsNotifications(false)
          await storage.removeItem(
            `${LOCAL_STORAGE_PREFIX}licenseStatus_${userId}`
          )
          await storage.removeItem(`${LOCAL_STORAGE_PREFIX}demo_${userId}`)
          const freshUser = await getUserAllInformation()
          if (freshUser) {
            let lastUpdateVal = new Date().toISOString()
            if (sessionState?.lastUpdate) {
              lastUpdateVal = sessionState.lastUpdate
            }
            freshUser.lastUpdate = lastUpdateVal
            await storage.setItem(
              `${LOCAL_STORAGE_PREFIX}userAll_${userId}`,
              freshUser
            )

            lastSessionState.current = null // Force fresh load on next page navigation
            activeUser = freshUser
            baseUpdateUserData({ newUserData: freshUser })
          } else {
            isRestoring.current = false
            logout()
            return
          }
        }

        // 4. Verify Status check
        let shouldVerify = true
        const verifyStatusCached = await storage.getItem(
          `${LOCAL_STORAGE_PREFIX}verifyStatus_${userId}`
        )
        if (verifyStatusCached) {
          const cachedStatus = verifyStatusCached
          if (cachedStatus.status && cachedStatus.date) {
            const statusDate = new Date(cachedStatus.date)
            const now = new Date()
            const diff = now - statusDate
            const diffInHours = diff / (1000 * 60 * 60)
            const isSameVersion =
              cachedStatus.version === process.env.REACT_APP_VERSION
            if (diffInHours < 12 && isSameVersion) {
              shouldVerify = false
            }
          }
        }
        if (shouldVerify) {
          verifyStatus().then(() => {
            storage.setItem(`${LOCAL_STORAGE_PREFIX}verifyStatus_${userId}`, {
              status: true,
              date: new Date().toISOString(),
              version: process.env.REACT_APP_VERSION,
            })
          })
        }

        // 5. Selected group loading
        if (!activeUser.isB2C || activeUser?.b2C?.groupId > 0) {
          let selectedGroup = null
          const groupDataString = await storage.getItem(
            `${LOCAL_STORAGE_PREFIX}selectedGroup_${userId}`
          )
          if (groupDataString) {
            try {
              selectedGroup = groupDataString
              const groupLastUpdate = selectedGroup.lastUpdate
              if (groupLastUpdate) {
                if (sessionState) {
                  const parentLastUpdate = sessionState?.lastUpdate
                  if (
                    parentLastUpdate &&
                    parentLastUpdate !== groupLastUpdate
                  ) {
                    selectedGroup = null
                  }
                } else {
                  const lastUpdateDate = moment(groupLastUpdate)
                  const now = moment()
                  const diff = now.diff(lastUpdateDate, "days")
                  if (diff > 1) {
                    selectedGroup = null
                  }
                }
              } else {
                selectedGroup = null
              }
            } catch (error) {
              console.log("Error parsing group data from local storage", error)
            }
          }
          if (selectedGroup === null) {
            let groupLastUpdate = new Date().toISOString()
            if (sessionState?.lastUpdate) {
              groupLastUpdate = sessionState?.lastUpdate
            }
            const result = await getStudentGroups()
            if (result) {
              if (result.length === 1) {
                selectedGroup = result[0]
              } else {
                const result2 = await getSelectedGroup()
                if (result2) {
                  selectedGroup = result2
                }
              }
            } else {
              const groups = activeUser.groups || []
              if (groups.length > 0) {
                selectedGroup = groups[0]
              }
            }
            if (selectedGroup) {
              selectedGroup.lastUpdate = groupLastUpdate
              await storage.setItem(
                `${LOCAL_STORAGE_PREFIX}selectedGroup_${userId}`,
                selectedGroup
              )
            }
          }
          if (selectedGroup) {
            saveMainTeacherTimeZone(selectedGroup)
            setSelectedGroupData(selectedGroup)
          }
        } else {
          const groups = activeUser?.groups || []
          if (groups.length > 0) {
            const selectedGroup = groups[0]
            saveMainTeacherTimeZone(selectedGroup)
            setSelectedGroupData(selectedGroup)
          }
        }
        setIsRestoreCompleted(true)
        isRestoring.current = false
      }

      checkSessionStalenessAndRestoreGroup()
    }

    if (STAFF_TYPES.includes(userData.userTypeId)) {
      setIsConfigurationCompleted(userData?.isConfigurationCompleted)
      if (isRestoreCompleted || isRestoring.current) {
        return
      }
      isRestoring.current = true
      setIsRestoreCompleted(true)
      isRestoring.current = false
    }
  }, [userData, isRestoreCompleted])

  const updateTextSettings = useCallback(
    (settings) => {
      setTextEditorSettings(settings)
      if (settings?.fontFamily >= 0) {
        const selectedFontStyle = fontFamilyOptions[settings.fontFamily]
        const selectedFontSize = fontOptions[settings.fontSize]
        const selectedBgColor = bgOptions[settings.backgroundColor]
        setBgColor(selectedBgColor?.bg || "white")
        setFontFamily(selectedFontStyle?.name || "Poppins Regular")
        setFontSize(selectedFontSize?.fontSize || "18px")
      }
      const userId = userData?.userId
      if (!userId || userId <= 0) return
      storage
        .getItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`)
        .then((user) => {
          if (user) {
            user.textEditorSettings = settings
            storage.setItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`, user)
          }
        })
    },
    [userData, setBgColor, setFontFamily, setFontSize]
  )

  const updateAudioSettings = useCallback(
    (settings) => {
      setAudioSettings(settings)
      const userId = userData?.userId
      if (!userId || userId <= 0) return
      storage
        .getItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`)
        .then((user) => {
          if (user) {
            user.audioSettings = settings
            storage.setItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`, user)
          }
        })
    },
    [userData]
  )

  const updateSelectedGroupData = useCallback(
    (newGroupData) => {
      setSelectedGroupData(newGroupData)
      const userId = userData?.userId
      if (!userId || userId <= 0) return
      storage.removeItem(`${LOCAL_STORAGE_PREFIX}licenseStatus_${userId}`)
      storage.setItem(
        `${LOCAL_STORAGE_PREFIX}selectedGroup_${userId}`,
        newGroupData
      )
      saveMainTeacherTimeZone(newGroupData)
    },
    [userData, saveMainTeacherTimeZone]
  )

  const updateUserData = useCallback(
    ({ token = "", newUserData = null }) => {
      if (newUserData !== null) {
        if (
          newUserData.allowSoundEffects !== undefined &&
          newUserData.allowSoundEffects !== audioSettings.enabledAudioEffects
        ) {
          updateUserAudioSettings({
            ...audioSettings,
            enabledAudioEffects: newUserData.allowSoundEffects,
          })
          setAudioSettings((prev) => ({
            ...prev,
            enabledAudioEffects: newUserData.allowSoundEffects,
          }))
        }
      }
      baseUpdateUserData({ token, newUserData })
    },
    [baseUpdateUserData, audioSettings, updateUserAudioSettings]
  )

  const isPlacementAssignation = useMemo(() => {
    return (
      selectedGroupData?.assignationTypeId === 3 ||
      selectedGroupData?.assignContentMode === 3
    )
  }, [selectedGroupData])

  const accountType = useMemo(() => {
    return !selectedGroupData?.groupTypeId ||
      !selectedGroupData?.educationLevelId
      ? GROUP_TYPES.SECONDARY_NEWCOMERS_REGULAR_CLASS
      : selectedGroupData?.educationLevelId > 1
      ? selectedGroupData?.groupTypeId
      : selectedGroupData?.gradeId === 6
      ? ELEMENTARY_TO_REGULAR[selectedGroupData?.groupTypeId]
      : selectedGroupData?.groupTypeId
  }, [selectedGroupData])

  const values = useMemo(
    () => ({
      selectedGroupData,
      audioSettings,
      textEditorSettings,
      answerTourOpened,
      tourSkiped,
      isPlacementAssignation,
      accountType,
      isConfigurationCompleted,
      setIsConfigurationCompleted,
      isRestoreCompleted,
      setTextEditorSettings: updateTextSettings,
      setTourSkiped,
      setAnswerTourOpened,
      setAudioSettings: updateAudioSettings,
      setSelectedGroupData: updateSelectedGroupData,
      updateUserData,
      logout,
    }),
    [
      selectedGroupData,
      audioSettings,
      textEditorSettings,
      answerTourOpened,
      tourSkiped,
      isPlacementAssignation,
      accountType,
      isConfigurationCompleted,
      isRestoreCompleted,
      updateTextSettings,
      updateAudioSettings,
      updateSelectedGroupData,
      updateUserData,
      logout,
    ]
  )

  return (
    <StudentAuthContext.Provider value={values}>
      <ContainerWrapper>{children}</ContainerWrapper>
    </StudentAuthContext.Provider>
  )
}

export default StudentAuthProvider
